import { useState, useCallback, ChangeEvent } from 'react';
import {
  useGetParentServiceIds,
  useGetChildrenServiceIds,
  useCheckIsChildren,
  useCheckIsParent
} from 'store/features/directories';

const getActiveParentServiceIds = ({
  getParentServiceIds,
  getChildrenServiceIds,
  checkIsChildren,
  servicesState,
  selectedId
}: {
  getParentServiceIds: ReturnType<typeof useGetParentServiceIds>,
  getChildrenServiceIds: ReturnType<typeof useGetChildrenServiceIds>,
  checkIsChildren: ReturnType<typeof useCheckIsChildren>,
  servicesState: ServicesState,
  selectedId: string
}) => Object.keys(getParentServiceIds(selectedId))
  .reverse()
  .reduce((acc, parentId) => {
    const inactiveParentId = !!Object.keys(getChildrenServiceIds(`${parentId}`))
      .find(id => (!servicesState[id] && !checkIsChildren(selectedId, id) && id !== selectedId && !acc[id]));
    return inactiveParentId ? acc : { ...acc, [parentId]: true };
  }, {} as { [key: string | number]: boolean; });

export interface ServicesState {
  [key: string]: boolean;
}

export type SetServices = (value: ServicesState) => void;
export type ChangeServices = (event: ChangeEvent<Element>, isChecked: boolean) => void;

export const useServicesState = (initValue?: ServicesState) => {
  const [services, setServices] = useState<ServicesState>(() => (initValue || {}));
  const getParentServiceIds = useGetParentServiceIds();
  const getChildrenServiceIds = useGetChildrenServiceIds();
  const checkIsChildren = useCheckIsChildren();
  const checkIsParent = useCheckIsParent();

  const onChangeServices: ChangeServices = useCallback((event, isChecked) => {
    // @ts-ignore
    const selectedId = event.target?.value as string;

    setServices((servicesState) => {
      if (isChecked) {
        return {
          ...servicesState,
          [selectedId]: true,
          ...getChildrenServiceIds(selectedId),
          ...getActiveParentServiceIds({ getParentServiceIds, getChildrenServiceIds, checkIsChildren, servicesState, selectedId })
        };
      }

      const selectedServiceIds = Object.keys(servicesState) as string[];
      return selectedServiceIds.reduce((acc, id) => ({
        ...acc,
        ...(!checkIsChildren(selectedId, id) && !checkIsParent(selectedId, id) && (id !== selectedId)) && { [id]: true }
      }), {});
    });
  }, [
    getChildrenServiceIds,
    getParentServiceIds,
    checkIsChildren,
    checkIsParent
  ]);

  return { services, onChangeServices, setServices } as const;
};
